/***
 * Model that represents the viewer's UserPreferences. No logic here present but custom initialization
 * can be added here.
 */
import Backbone from 'backbone';

import Personalizer from 'personalizer';
import Localizer from 'localizer';
import DateFormatter from 'utils/date/DateFormatter';
import LocaleMapper from 'utils/LocaleMapper';
import moment from 'moment';
import * as sendTypes from '../../../standaloneComponents/SendToDevice/SendToDeviceUtils';
import { setCookie, getCookie } from 'utils/CookieUtil';

var Events = {
    ON_CULTURE_READY: 'ON_CULTURE_READY' // Event fired when the selected locale has had its profile fetched from the server.
};

var LEGACY_LOCALE_COOKIE_NAME = 'PrimaryGarminUserLocalePref'; // No longer being used. Legacy
var GARMIN_LOCALE_PREF = 'GarminLocalePref';
var GARMIN_USER_PREFS = 'GarminUserPrefs';

var LAST_DASHBOARD_PREF_NAME = 'LAST_DASHBOARD';
var VIEWER_SEEN_TUTORIAL_KEY = 'VIEWER_SEEN_TUTORIAL';

var UserPreferences = Backbone.Model.extend({
    backend: 'gcapi',

    url: function() {
        var displayName = this.get('displayName') || '';
        return '/userprofile-service/userprofile/settings/' + displayName;
    },

    initialize: function(args) {
        this.getLocaleFromCookie = args && args.getLocaleFromCookie; // If true, this determines the locale from a cookie rather than from preferences

        this.bind('change', this.onFetchSuccess, this);

        this.migrateToNewPreferenceKey();
    },

    getDisplayName: function() {
        return this.get('displayName');
    },

    /**
     * Called after a successful fetch. Sets up the personalizer and localizer with the correct user profile
     * information.
     */
    onFetchSuccess: function() {
        this.updateLocalizerAndPersonalizer();
    },

    save: function(attributes, cb) {
        let options = {};
        let clonedModel = this.clone();
        clonedModel.unset('hydrationMeasurementUnit');
        clonedModel.unset('hydrationContainers');

        if (typeof cb === 'function') {
            options.url = '/userprofile-service/userprofile/settings/';
            options.type = 'PUT';
            options.data = JSON.stringify(clonedModel);
            options.contentType = 'application/json';
            options.complete = function(jqXHR) {
                // Because some end points send back an empty responseText parameter of type JSON,
                // -> a parse error occurs causing jQuery's success callback to not fire, this is a hack to get around that
                // -> the request itself can be successful but it's the parsing of the non-existent JSON that's the issue
                if (jqXHR.status >= 200 && jqXHR.status < 300) {
                    cb();
                } else {
                    cb(true, jqXHR);
                }
            };

            Backbone.Model.prototype.save.call(this, attributes, options);
        }
    },

    /**
     * Updates the personalizer and localizer with the current settings.
     */
    updateLocalizerAndPersonalizer: function() {
        Personalizer.setMeasurementSystem(this.getMeasurementSystem());
        Personalizer.setNumberSeparator(this.getNumberFormat().toUpperCase());
        Personalizer.setDateFormat(this.getDateFormatKey());
        Personalizer.setTimeFormat(this.getTimeFormatKey());

        Localizer.setCurrentDateFormatKey(this.getDateFormatKey());

        DateFormatter.setDateFormat(this.getDateFormatKey());
        DateFormatter.setTimeFormat(this.getTimeFormatKey());
    },

    /**
     * Returns the user's preferred measurement system.
     */
    getMeasurementSystem: function() {
        return this.get('measurementSystem');
    },

    /**
     * Returns the user's preferred golf distance units.
     */
    getGolfDistanceUnit: function() {
        return this.get('golfDistanceUnit');
    },

    /**
     * Returns the user's preferred heart rate display type.
     */
    getHeartRateFormatKey: function() {
        return this.get('heartRateFormat').formatKey;
    },

    /**
     * Returns the user's preferred power display type.
     */
    getPowerDisplayFormatKey: function() {
        return this.get('powerFormat').formatKey;
    },

    /**
     * Returns the user's preferred first day of the week.
     */
    getFirstDayOfWeek: function() {
        return this.get('firstDayOfWeek').dayName;
    },

    /**
     * Returns the Locale for the user.
     */
    getLocale: function() {
        var preferredLocale = this.get('preferredLocale');

        if (this.getLocaleFromCookie) {
            var locale = getCookie(GARMIN_USER_PREFS) || preferredLocale || 'en';

            // The app currently only supports country for Chinese and Portuguese, so trim that off
            if (locale.substr(0, 2) != 'zh' && locale.substr(0, 2) != 'pt') {
                locale = locale.substr(0, 2);
            }

            return locale;
        } else {
            return preferredLocale;
        }
    },

    /**
     * Returns the Locale Country for the user.
     */
    getLocaleCountry: function() {
        var preferredLocale = this.getFullLocale();

        if (preferredLocale) {
            var preferredCountry = preferredLocale.substring(preferredLocale.indexOf('_') + 1);
            return preferredCountry.toLowerCase();
        }

        return 'us';
    },

    /**
     * Returns the full locale string, including the language and the country codes.
     * Ex. en_US
     */
    getFullLocale: function() {
        var connectLocale = this.getLocale(); // this is the short, language only locale

        return LocaleMapper.getFullLocale(connectLocale);
    },

    /**
     * Returns the date format from this Preferences object.
     */
    getDateFormatKey: function() {
        return this.get('dateFormat').formatKey;
    },

    /**
     * Returns the time format from this Preferences object.
     */
    getTimeFormatKey: function() {
        return this.get('timeFormat').formatKey;
    },

    /**
     * Returns the number format for numbers. (uses commas as decimals or periods).
     */
    getNumberFormat: function() {
        return this.get('numberFormat');
    },

    /**
     * Called when the culture information has been asyncronously loaded. Raises an event that other objects
     * can listen for.
     */
    onLocaleCultureReady: function() {
        this.trigger(Events.ON_CULTURE_READY);
    },

    /**
     * Gets the dashboard that the user last accessed. Currently is stored in a cookie. Returns null if not
     * found.
     */
    getLastDashboard: function() {
        var lastDashboard = this.getLocalStoragePreference(LAST_DASHBOARD_PREF_NAME);
        if (lastDashboard) {
            return lastDashboard;
        }

        return null;
    },

    /**
     * Sets the dashboard that the user last accessed. Currently is stored in a cookie.
     */
    setLastDashboard: function(dashboardId) {
        this.setLocalStoragePreference(LAST_DASHBOARD_PREF_NAME, dashboardId);
    },

    /**
     * Originally, the preference key was driven by displayname, but the displayname can change, so this was
     * a poor choice. We migrate the settings over to user profile ID instead.
     */
    migrateToNewPreferenceKey: function() {
        try {
            var oldPreferenceKey = this.getOldLocalStoragePreferenceKey();
            var oldPreferences = localStorage[oldPreferenceKey];
            if (typeof oldPreferences == 'undefined' || oldPreferences == null) {
                return;
            }

            // We have old preferences. Make sure we don't have new.
            var preferenceKey = this.getLocalStoragePreferenceKey();
            var preferences = localStorage[preferenceKey];
            if (typeof preferences == 'undefined' || preferences == null) {
                // Store the preferences under the new key
                localStorage[preferenceKey] = oldPreferences;
            }
        } catch (e) {
            console.log(e);
        }

        // Eventually, we will want to remove this key, but this could cause some issues switching back and forth
        // between environments so we leave it for now.
        // localStorage.removeItem(oldPreferenceKey);
    },

    getOldLocalStoragePreferenceKey: function() {
        var displayName = this.get('displayName');
        if (displayName) {
            return 'pref-' + displayName;
        } else {
            return null;
        }
    },

    setPreferenceKey: function(preferenceKey) {
        this.preferenceKey = preferenceKey;
        this.migrateToNewPreferenceKey();
    },

    getLocalStoragePreferenceKey: function() {
        if (this.preferenceKey) {
            return 'pref-' + this.preferenceKey;
        } else {
            return null;
        }
    },

    setLocalStoragePreference: function(key, value) {
        if (typeof localStorage == 'undefined') {
            throw 'No Local Storage';
        }

        if (!this.getLocalStoragePreferenceKey()) {
            throw 'Preference set for unauthenticated user';
        }

        // Get the users preferences object
        var preferences = localStorage[this.getLocalStoragePreferenceKey()];
        if (preferences) {
            preferences = JSON.parse(preferences);
        } else {
            preferences = {};
        }

        preferences[key] = value;

        preferences = JSON.stringify(preferences);
        localStorage[this.getLocalStoragePreferenceKey()] = preferences;
    },

    getLocalStoragePreference: function(key) {
        if (typeof localStorage == 'undefined') {
            throw 'No Local Storage';
        }

        if (!this.getLocalStoragePreferenceKey()) {
            throw 'Preference retrievel for unauthenticated user';
        }

        // Get the users preferences object
        var preferences = localStorage[this.getLocalStoragePreferenceKey()];
        if (typeof preferences == 'undefined' || preferences == null) {
            return null;
        }

        preferences = JSON.parse(preferences);
        return preferences[key];
    },

    getLaunchGEFromCookies() {
        if (typeof localStorage !== 'undefined' && localStorage.getItem(sendTypes.LAUNCH_GE_PREFERENCE)) {
            return localStorage.getItem(sendTypes.LAUNCH_GE_PREFERENCE) === 'true';
        }
        if (getCookie(sendTypes.LAUNCH_GE_PREFERENCE)) {
            return getCookie(sendTypes.LAUNCH_GE_PREFERENCE) === 'true';
        }
        return true;
    },

    setLaunchGECookies(launchGE) {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(sendTypes.LAUNCH_GE_PREFERENCE, launchGE);
            return;
        }
        setCookie(sendTypes.LAUNCH_GE_PREFERENCE, launchGE);
    },

    getNewsFeedPeopleFilterFromCookies() {
        if (this.getLocalStoragePreference('newsFeedPeopleFilter')) {
            return this.getLocalStoragePreference('newsFeedPeopleFilter');
        } else {
            return 'ALL';
        }
    },

    setNewsFeedPeopleFilterCookies(peopleFilter) {
        this.setLocalStoragePreference('newsFeedPeopleFilter', peopleFilter);
    },

    getNewsFeedActivityFilterFromCookies() {
        if (this.getLocalStoragePreference('newsFeedActivityFilter')) {
            return this.getLocalStoragePreference('newsFeedActivityFilter');
        }
    },

    setNewsFeedActivityFilterCookies(activityFilter) {
        this.setLocalStoragePreference('newsFeedActivityFilter', activityFilter);
    },

    getNewsFeedLastReadTimeFromCookies() {
        if (this.getLocalStoragePreference('newsFeedLastReadTime')) {
            return this.getLocalStoragePreference('newsFeedLastReadTime');
        } else {
            return this.getNewsFeedBubbleDateTime(true);
        }
    },

    setNewsFeedLastReadTimeCookies() {
        this.setLocalStoragePreference('newsFeedLastReadTime', this.getNewsFeedBubbleDateTime(false));
    },

    is24HourFormat: function() {
        return this.get('timeFormat').formatKey === 'time_twenty_four_hr';
    },

    getNewsFeedBubbleDateTime(is30DaysBack) {
        const format = 'YYYY-MM-DDTHH:mm:ss.S';
        if (is30DaysBack) {
            return moment
                .utc()
                .add('days', -30)
                .format(format);
        } else {
            return moment.utc().format(format);
        }
    }
});

UserPreferences.writeLegacyLocaleCookie = function(locale) {
    if (locale.substr(0, 2) == 'zh') {
        locale = locale.replace('-', '_');
    } else {
        locale = locale.substr(0, 2);
    }

    // Ex: connect.garmin.com -> garmin.com || connectcn.garmin.cn -> garmin.cn
    function getDomainName() {
        var hostParts = window.location.hostname.split('.');
        if (hostParts.length === 3) {
            hostParts.shift();
        }
        return hostParts.join('.');
    }
    var domainName = getDomainName();

    setCookie(LEGACY_LOCALE_COOKIE_NAME, locale, {
        path: '/',
        domain: domainName
    });
};

UserPreferences.writeGarminPrefsCookie = function(locale) {
    function getDomainName() {
        var hostParts = window.location.hostname.split('.');
        if (hostParts.length === 3) {
            hostParts.shift();
        }
        return hostParts.join('.');
    }
    var domainName = getDomainName();

    setCookie(GARMIN_USER_PREFS, locale, { path: '/', domain: domainName });
};

UserPreferences.updateLegacySessionLocale = function(locale) {
    jQuery.ajax({
        url: '/user/locale/' + locale,
        type: 'POST'
    });
};

UserPreferences.Events = Events;

export default UserPreferences;
